import React, { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./SupportWidget.css";
import { CustomerServiceOutlined, CloseOutlined } from "@ant-design/icons";

const CATEGORIES = [
  "Bug",
  "Feature Request",
  "Performance",
  "Access/Auth",
  "Data Issue",
  "Integration",
  "Other",
];

const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const PANEL_WIDTH = 320;
const PANEL_HEIGHT = 480;
const GAP = 12;

export default function SupportWidget({
  apiKey,
  apiUrl = "/api",
  appCode,
  userEmail = "",
  userName = "",
  extraContext = {},
  accentColor = "#4f7fff",
  companyName = "Support",
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form");
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [panelPosition, setPanelPosition] = useState({
    top: 0,
    left: 0,
    width: 360,
    height: 480,
  });

  const widgetRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Bug",
    priority: "Medium",
    email: userEmail,
    name: userName,
  });

  const [submittedTicket, setSubmittedTicket] = useState(null);

  const meta = document.querySelector('meta[name="app-version"]');

  const getOS = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Win")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    if (ua.includes("Android")) return "Android";
    if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
    return "Unknown";
  };

  const getBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
    if (ua.includes("Edg")) return "Edge";
    return "Unknown";
  };

  const captureContext = () => ({
    url: window.location.href,
    appVersion: meta ? meta.content : "",
    userAgent: navigator.userAgent,
    os: getOS(),
    browser: getBrowser(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    extraMeta: extraContext,
  });

  const field = (key, val) =>
    setForm((f) => ({
      ...f,
      [key]: val,
    }));

  const calculatePosition = () => {
    if (!widgetRef.current) return;

    const rect = widgetRef.current.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const panelWidth = Math.min(420, vw - 24);
    const panelHeight = Math.min(480, vh - 24);

    const spaces = {
      top: rect.top,
      bottom: vh - rect.bottom,
      left: rect.left,
      right: vw - rect.right,
    };

    let top = rect.bottom + GAP;
    let left = rect.left;

    // Prefer opening on right (old UI feel)
    if (spaces.right >= panelWidth) {
      top = rect.top;
      left = rect.right + GAP;
    }
    // then left
    else if (spaces.left >= panelWidth) {
      top = rect.top;
      left = rect.left - panelWidth - GAP;
    }
    // then bottom
    else if (spaces.bottom >= panelHeight) {
      top = rect.bottom + GAP;
      left = rect.left;
    }
    // then top
    else if (spaces.top >= panelHeight) {
      top = rect.top - panelHeight - GAP;
      left = rect.left;
    }
    // fallback centered
    else {
      top = (vh - panelHeight) / 2;
      left = (vw - panelWidth) / 2;
    }

    // Keep inside viewport
    top = Math.max(12, Math.min(top, vh - panelHeight - 12));
    left = Math.max(12, Math.min(left, vw - panelWidth - 12));

    setPanelPosition({
      top,
      left,
      width: panelWidth,
      height: panelHeight,
    });
  };

  useLayoutEffect(() => {
    if (open) {
      calculatePosition();

      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition, true);
    }

    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.description) {
      setError("Title and description are required.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const res = await fetch(`${apiUrl}/widget/ticket`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-widget-api-key": apiKey,
        },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          category: form.category,
          priority: form.priority,
          appCode,
          reportedByEmail: form.email,
          reportedByName: form.name,
          context: captureContext(),
        }),
      });

      const data = await res.json();

      if (data.success) {
        setSubmittedTicket(data.ticketNumber);
        setStep("success");

        setForm((f) => ({
          ...f,
          title: "",
          description: "",
          category: "Bug",
          priority: "Medium",
        }));
      } else {
        setError(data.message || "Failed to submit ticket.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setStep("form"), 300);
  };

  return (
    <>
      <div className="sw-root" ref={widgetRef}>
        <div
          className={`sw-fab ${open ? "sw-fab--open" : ""}`}
          onClick={() => setOpen((o) => !o)}
          role="button"
        >
          {open ? <CloseOutlined /> : <CustomerServiceOutlined />}
        </div>
      </div>

      {open &&
        createPortal(
          <div
            className="sw-panel sw-panel--open"
            style={{
              top: panelPosition.top,
              left: panelPosition.left,
              "--accent": accentColor,
            }}
          >
            <div className="sw-panel__header">
              <div>
                <div className="sw-panel__title">
                  {config ? config.clientName : companyName} Support
                </div>
              </div>
              <button className="sw-close" onClick={handleClose}>
                ✕
              </button>
            </div>

            <div className="sw-panel__body">
              {step === "form" && (
                <form onSubmit={handleSubmit} className="sw-form">
                  {error && <div className="sw-error">{error}</div>}

                  <div className="sw-field">
                    <label>Your Name</label>
                    <input
                      type="text"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={(e) => field("name", e.target.value)}
                    />
                  </div>

                  <div className="sw-field">
                    <label>
                      Email <span className="sw-req">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={(e) => field("email", e.target.value)}
                      required
                    />
                  </div>

                  <div className="sw-field">
                    <label>
                      Issue Title <span className="sw-req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Brief description of the problem"
                      value={form.title}
                      onChange={(e) => field("title", e.target.value)}
                      required
                    />
                  </div>

                  <div className="sw-field">
                    <label>Category</label>
                    <select
                      value={form.category}
                      onChange={(e) => field("category", e.target.value)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sw-field">
                    <label>Severity</label>
                    <div className="sw-priority-grid">
                      {PRIORITIES.map((p) => (
                        <button
                          key={p}
                          type="button"
                          className={`sw-priority-btn ${
                            form.priority === p ? "active" : ""
                          }`}
                          onClick={() => field("priority", p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sw-field">
                    <label>
                      Describe the Issue <span className="sw-req">*</span>
                    </label>
                    <textarea
                      rows={5}
                      placeholder="What happened? Steps to reproduce, what you expected vs what occurred..."
                      value={form.description}
                      onChange={(e) => field("description", e.target.value)}
                      required
                    />
                  </div>

                  <div className="sw-context-note">
                    Browser, OS and page context will be captured automatically.
                  </div>

                  <button
                    type="submit"
                    className="sw-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit Ticket"}
                  </button>
                </form>
              )}

              {step === "success" && (
                <div className="sw-success">
                  <h3>Ticket Submitted!</h3>
                  <p>Reference: {submittedTicket}</p>
                  <button className="sw-submit" onClick={() => setStep("form")}>
                    Submit Another
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
